/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  createClient,
  type MatrixClient,
} from "matrix-js-sdk";
import {
  completeAuthorizationCodeGrant,
  generateOidcAuthorizationUrl,
} from "matrix-js-sdk/lib/oidc/authorize";
import { type OidcClientConfig } from "matrix-js-sdk/lib/oidc";
import {
  registerOidcClient,
  type OidcRegistrationClientMetadata,
} from "matrix-js-sdk/lib/oidc/register";
import { secureRandomString } from "matrix-js-sdk/lib/randomstring";

import { Config } from "../config/Config";
import { initClient } from "../utils/matrix";
import { type Session } from "../ClientContext";

const OIDC_POST_LOGIN_PATH_KEY = "element-call-oidc-post-login-path";

export interface OidcCallbackParams {
  code: string;
  state: string;
  responseMode: "query" | "fragment";
}

export function getOidcCallbackParamsFromUrl(
  href: string,
): OidcCallbackParams | undefined {
  const url = new URL(href);
  const queryCode = url.searchParams.get("code");
  const queryState = url.searchParams.get("state");
  if (queryCode && queryState) {
    return { code: queryCode, state: queryState, responseMode: "query" };
  }

  const fragment = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  const fragmentParams = new URLSearchParams(fragment);
  const fragmentCode = fragmentParams.get("code");
  const fragmentState = fragmentParams.get("state");
  if (fragmentCode && fragmentState) {
    return {
      code: fragmentCode,
      state: fragmentState,
      responseMode: "fragment",
    };
  }

  return undefined;
}

export function clearOidcCallbackParamsFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  url.searchParams.delete("iss");
  if (url.hash.includes("code=") || url.hash.includes("state=")) {
    url.hash = "";
  }
  window.history.replaceState({}, document.title, url.pathname + url.search);
}

export function saveOidcPostLoginPath(path: string): void {
  sessionStorage.setItem(OIDC_POST_LOGIN_PATH_KEY, path);
}

export function consumeOidcPostLoginPath(): string {
  const path = sessionStorage.getItem(OIDC_POST_LOGIN_PATH_KEY) || "/";
  sessionStorage.removeItem(OIDC_POST_LOGIN_PATH_KEY);
  return path;
}

export async function startOidcLogin(homeserverUrl: string): Promise<void> {
  const authClient = createClient({ baseUrl: homeserverUrl });
  const delegatedAuthConfig = await authClient.getAuthMetadata();
  const clientId = await getOidcClientId(delegatedAuthConfig);
  const redirectUri = getOidcCallbackUrl().href;
  const responseMode = delegatedAuthConfig.response_modes_supported?.includes(
    "fragment",
  )
    ? "fragment"
    : "query";

  const authorizationUrl = await generateOidcAuthorizationUrl({
    metadata: delegatedAuthConfig,
    redirectUri,
    clientId,
    homeserverUrl,
    nonce: secureRandomString(16),
    responseMode,
  });

  window.location.assign(authorizationUrl);
}

export async function completeOidcLogin(
  oldClient?: MatrixClient,
): Promise<[MatrixClient, Session] | undefined> {
  const params = getOidcCallbackParamsFromUrl(window.location.href);
  if (!params) return undefined;

  const { homeserverUrl, tokenResponse } = await completeAuthorizationCodeGrant(
    params.code,
    params.state,
    params.responseMode,
  );

  const whoamiClient = createClient({
    baseUrl: homeserverUrl,
    accessToken: tokenResponse.access_token,
  });
  const whoami = await whoamiClient.whoami();
  const userId = whoami.user_id;
  const deviceId = whoami.device_id;
  if (!userId || !deviceId) {
    throw new Error("OIDC login did not return a Matrix user id and device id");
  }

  await oldClient?.logout(true);
  const client = await initClient(
    {
      baseUrl: homeserverUrl,
      accessToken: tokenResponse.access_token,
      userId,
      deviceId,
    },
    false,
  );

  return [
    client,
    {
      user_id: userId,
      access_token: tokenResponse.access_token,
      refresh_token: tokenResponse.refresh_token,
      device_id: deviceId,
      passwordlessUser: false,
      auth_method: "oidc",
    },
  ];
}

export async function revokeOidcSession(
  client: MatrixClient,
  refreshToken?: string,
): Promise<void> {
  const delegatedAuthConfig = await client.getAuthMetadata();
  const clientId = await getOidcClientId(delegatedAuthConfig);
  const accessToken = client.getAccessToken() ?? undefined;

  for (const [token, tokenTypeHint] of [
    [accessToken, "access_token"],
    [refreshToken, "refresh_token"],
  ] as const) {
    if (!token) continue;

    const body = new URLSearchParams({
      client_id: clientId,
      token,
      token_type_hint: tokenTypeHint,
    });
    const response = await fetch(delegatedAuthConfig.revocation_endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!response.ok) {
      throw new Error(`OIDC token revocation failed (${response.status})`);
    }
  }
}

export async function getOidcClientId(
  delegatedAuthConfig: OidcClientConfig,
): Promise<string> {
  const staticClientId = getStaticOidcClientId(delegatedAuthConfig.issuer);
  if (staticClientId) return staticClientId;

  try {
    return await registerOidcClient(
      delegatedAuthConfig,
      getOidcClientRegistrationMetadata(),
    );
  } catch (error) {
    const cause = error instanceof Error ? `: ${error.message}` : "";
    throw new Error(
      `OIDC dynamic client registration failed for ${delegatedAuthConfig.issuer}. ` +
        "Configure oidc_static_clients for this issuer or enable dynamic " +
        `registration for this Element Call origin${cause}`,
    );
  }
}

function getStaticOidcClientId(issuer: string): string | undefined {
  const staticClients = Config.get().oidc_static_clients;
  if (!staticClients) return undefined;

  const issuerWithTrailingSlash = issuer.endsWith("/") ? issuer : `${issuer}/`;
  const issuerWithoutTrailingSlash = issuer.endsWith("/")
    ? issuer.slice(0, -1)
    : issuer;
  return (
    staticClients[issuerWithTrailingSlash]?.client_id ??
    staticClients[issuerWithoutTrailingSlash]?.client_id ??
    staticClients[issuer]?.client_id
  );
}

function getOidcClientRegistrationMetadata(): OidcRegistrationClientMetadata {
  const metadata = Config.get().oidc_metadata;
  const callbackUrl = getOidcCallbackUrl();
  return {
    clientName: metadata?.client_name ?? "Element Call",
    clientUri: metadata?.client_uri ?? window.location.origin,
    redirectUris: [callbackUrl.href],
    logoUri:
      metadata?.logo_uri ??
      new URL("/favicon.ico", window.location.origin).href,
    applicationType: "web",
    contacts: metadata?.contacts,
    tosUri: metadata?.tos_uri ?? Config.get().ssla,
    policyUri: metadata?.policy_uri,
  };
}

function getOidcCallbackUrl(): URL {
  const url = new URL(window.location.origin + window.location.pathname);
  url.searchParams.set("no_universal_links", "true");
  return url;
}
